import React, { useMemo, useState, useCallback, useEffect } from "react";
import TabGridAndFormLayout from "../../../Components/ProForms/ProFormsLayout/TabGridAndFormLayout";
import ProGrid from "../../../Components/ProGrid/v2";
import { changeMode, changeModeForField, fieldTruePermission, prepareDefaultValues, prepareInitialConfig } from "../../../../utils/formHelper";
import useColumn from "../../../../utils/customHooks/useColumns";
import usePageState from "../../../../utils/customHooks/usePageState";
import { useForm } from "react-hook-form";
import { STAUTES } from "../../../../utils/constant";
import ProTextInput from "../../../Components/Inputs/TextField";
import { createODataQuery } from "../../../../utils/services/oData";
// import { ADD_RAP_RTI, DELETE_RAP_RTI, GET_ALL_RAP_RTI, UPDATE_RAP_RTI } from "../../../../utils/services/apiPath";
import { deleteApi, getApi, postApi, putApi } from "../../../../utils/services";
import { useParams } from "react-router-dom";
import NameCell from "../../../Components/ProGrid/components/NameCell";
import DeleteCell from "../../../Components/ProGrid/components/DeleteCell";
import { errorToast, successToast } from "../../../../utils/toastHelper";
import { ADMIN_RAP_RTI } from "../../../../utils/services/apiPath";
import { apprenticeRapPermissionSelector } from "../../../store/features/permissions/permissionsSlice";
import usePermission from "../../../../utils/customHooks/usePermission";

const RTIDescriptionFormConfing = {
  title: {
    label: "Title",
    rules: { required: "Title is required!" }
  },
  description: {
    label: "Description",
    fieldProps: { multiline: true, rows: 4 },
    rules: { required: "Description is required!" }
  },
  approximateHours: {
    label: "Approximate Hours",
    rules: { required: "Approximate Hours is required!" }
  },
  creditHours: {
    label: "Credit Hours",
    rules: { required: "Credit Hours is required!" }
  }
};
export default function RelatedInstructionDescriptions() {
  const { id } = useParams();
  const [showGrid, setShowGrid] = useState(true);
  const [formConfig, setFormConfig] = useState(prepareInitialConfig(RTIDescriptionFormConfing));
  const [rtiDescriptionData, setRtiDescriptionData] = useState({});
  const [actionData, setActionData] = useState({});
  const readOnly = localStorage.getItem("viewingReadOnlyRapInfo") == "true";
  const [isSaving, setIsSaving] = useState(false);
  const {
    currentPage,
    pageSize,
    data: rtiData,
    rowCount,
    status,
    sortModel,
    searchString,
    setPageState,
    setPagination,
    setStatus,
    setSortModel,
    setSearchString
  } = usePageState();

  const { "Related Instruction Descriptions": rapRtdPermission } = usePermission(apprenticeRapPermissionSelector, "Related Instruction Descriptions");
  const { canView, canAdd, canEdit, canDelete, canExport } = rapRtdPermission || {};

  const columns = useColumn(
    [
      {
        field: "rtiTitle",
        headerName: "Title",
        renderCell: params => <NameCell params={params} onClick={onUserClick} />
      },
      {
        field: "approximateHours",
        headerName: "Approximate Hours"
      },
      {
        field: "creditHours",
        headerName: "Credit Hours"
      },
      {
        field: "rapRtiId",
        headerName: "Actions",
        isDeleteColumn: true,
        disableExport: true,
        width: 80,
        renderCell: params => <DeleteCell params={params} onDelete={onDelete} title={"Delete RTI Description"} disabled={readOnly} />
      }
    ],
    canDelete
  );
  const { handleSubmit, control, setValue } = useForm({ defaultValues: prepareDefaultValues(RTIDescriptionFormConfing) });
  const isEditModeOn = useMemo(() => Object.values(formConfig).some(({ mode }) => mode === "edit"), [formConfig]);

  const onEdit = () => {
    setFormConfig(changeMode("edit"));
  };
  const handleReadValueClick = name => setFormConfig(changeModeForField(name, "edit"));

  const resetForm = () => {
    setActionData({});
    setShowGrid(true);
    setFormConfig(changeMode("read"));
  };

  const onCancel = () => {
    resetForm();
    setShowGrid(true);
  };

  const onUserClick = params => {
    setShowGrid(false);
    setActionData(params.row);
    getClientUserData(params.row);
  };

  const addButtonAction = () => {
    Object.keys(formConfig).forEach(field => setValue(field, null));
    onEdit();
    setShowGrid(false);
  };

  const onSave = async data => {
    if (isSaving) return;
    setIsSaving(true);
    const payload = {
      rapInfoId: id,
      rtiDescription: data?.description,
      rtiTitle: data?.title,
      approximateHours: parseInt(data?.approximateHours),
      creditHours: parseInt(data?.creditHours)
    };
    const methodApi = actionData?.rapRtiId ? putApi : postApi;
    const { error } = await methodApi(actionData?.rapRtiId ? `${ADMIN_RAP_RTI}/${actionData?.rapRtiId}` : ADMIN_RAP_RTI, payload);
    if (error) return errorToast(error);
    getRTIDescription();
    setIsSaving(false);
    resetForm();
    successToast(`RTI Description ${actionData?.rapRtiId ? "updated" : "added"} successfully.`);
  };

  const onDelete = async id => {
    const { error } = await deleteApi(`${ADMIN_RAP_RTI}/${id}`);
    if (error) return errorToast(error);
    getRTIDescription();
    setShowGrid(true);
    successToast("RTI Description deleted successfully.");
  };

  const getClientUserData = updateData => {
    const data = {
      description: updateData?.rtiDescription,
      title: updateData?.rtiTitle,
      approximateHours: updateData?.approximateHours,
      creditHours: updateData?.creditHours
    };
    setRtiDescriptionData(data);
    Object.keys(formConfig).forEach(field => setValue(field, data[field]));
  };

  const getRTIDescription = useCallback(async () => {
    setStatus(STAUTES.LOADING);
    const searchableFields = ["rtiTitle", "cast(approximateHours, 'Edm.String')", "cast(creditHours, 'Edm.String')"];
    const { data, totalRecords, error } = await getApi(
      ADMIN_RAP_RTI + `${createODataQuery(searchableFields, [], "", searchString, sortModel, pageSize, currentPage)}&rapInfoId=${id}`
    );
    setStatus(STAUTES.IDLE);
    if (error) return errorToast(error);
    setPageState(prevPageInfo => ({ ...prevPageInfo, data: data || [], rowCount: totalRecords }));
  }, [currentPage, pageSize, sortModel, searchString, setPageState, setStatus]);

  useEffect(() => {
    getRTIDescription();
  }, [getRTIDescription]);

  const defaultFormProps = { control, formValues: rtiDescriptionData, handleReadValueClick, permission: { canView, canAdd, canEdit } };
  return (
    <TabGridAndFormLayout
      showGrid={showGrid}
      title={`${actionData?.raprtiGuid ? "Edit" : "Add"} Related Instruction Descriptions`}
      backAction={() => resetForm()}
      beingEdited={isEditModeOn}
      onEdit={onEdit}
      backLabel={"Go Back to Related Instruction Descriptions"}
      canView={canView}
      hideEdit={readOnly || !canEdit}
      onSave={handleSubmit(onSave)}
      onCancel={onCancel}
      isSaving={isSaving}
    >
      {showGrid && (
        <ProGrid
          title="Related Instruction Descriptions"
          loading={status === STAUTES.LOADING}
          options={{
            getRowId: row => row.rapRtiId,
            rowCount: rowCount,
            paginationMode: "server",
            paginationModel: { pageSize: pageSize, page: currentPage },
            onPaginationModelChange: setPagination,
            sortingMode: "server",
            onSortModelChange: setSortModel
          }}
          columns={columns}
          rows={rtiData}
          searchMode={!!searchString}
          searchModel={{ defaultValue: searchString, handleDebounce: setSearchString }}
          addButtonLabel="+ Add RTI Description"
          hideExport={!canExport}
          hideAddButton={!canAdd}
          addButtonAction={addButtonAction}
        />
      )}
      {!showGrid && (
        <>
          <ProTextInput {...defaultFormProps} {...formConfig.title} />
          <ProTextInput {...defaultFormProps} {...formConfig.approximateHours} type="number" />
          <ProTextInput {...defaultFormProps} {...formConfig.creditHours} type="number" />
          <ProTextInput {...defaultFormProps} {...formConfig.description} />
        </>
      )}
    </TabGridAndFormLayout>
  );
}
